import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useScreen } from './useScreen';
import { useTheme } from './useTheme';
import UserAvatar from './UserAvatar';

const C = { turquesa:'#5dc1b9', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)', triGrad:'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b)' };
const font = "'DM Sans', sans-serif";
const TIER_LABELS = { free_observer:'Free Observer', doer:'Doer', spark_seeker:'Doer', momentum_builder:'Momentum', growth_mastermind:'Growth', onsite_mexico:'Onsite', admin:'Admin' };

export default function SidebarLayout({ menuItems, activeSection, onSectionChange, children, title }) {
  const navigate = useNavigate();
  const { user, profile, membership, signOut } = useAuth();
  const { isMobile, w } = useScreen();
  const hideSidebar = w < 900;
  const t = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const nombre = profile?.full_name?.split(' ')[0] || 'ahí';

  const selectSection = (id) => {
    onSectionChange(id);
    setMenuOpen(false);
  };

  const sidebarContent = (
    <>
      {/* User info */}
      <div style={{ padding:'20px 16px', borderBottom:`0.5px solid ${t.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <UserAvatar url={profile?.avatar_url} name={nombre} size={36}/>
          <div>
            <p style={{ fontSize:14, fontWeight:500, margin:0, color:t.text }}>{nombre}</p>
            <span style={{ fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:10, background:C.triGrad, color:'#fff' }}>{TIER_LABELS[membership]||'Miembro'}</span>
          </div>
        </div>
      </div>
      {/* Menu items */}
      <div style={{ padding:'8px 0', flex:1 }}>
        {menuItems.map(item => {
          if (item.type === 'link') {
            return (
              <button key={item.id} onClick={()=>{navigate(item.href);setMenuOpen(false)}} style={{
                display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 16px',
                background:'none', border:'none', cursor:'pointer', fontFamily:font,
                fontSize:13, color:t.textSecondary, textAlign:'left',
              }}>
                <span style={{ fontSize:16 }}>{item.emoji}</span>{item.label}
              </button>
            );
          }
          const active = activeSection === item.id;
          return (
            <button key={item.id} onClick={()=>selectSection(item.id)} style={{
              display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 16px',
              background: active ? C.turquesa+'12' : 'none',
              borderTop:'none', borderRight:'none', borderBottom:'none',
              borderLeft: active ? '3px solid' : '3px solid transparent',
              borderImage: active ? 'linear-gradient(180deg, #5dc1b9, #FDBE02, #eb636b) 1' : 'none',
              cursor:'pointer', fontFamily:font,
              fontSize:13, fontWeight:active?500:400, color:active?C.turquesa:t.text, textAlign:'left',
              transition:'background 0.2s, box-shadow 0.2s',
            }}>
              <span style={{ fontSize:16 }}>{item.emoji}</span>{item.label}
            </button>
          );
        })}
      </div>
      {/* Bottom */}
      <div style={{ padding:'12px 16px', borderTop:`0.5px solid ${t.border}` }}>
        <button onClick={async()=>{await signOut();navigate('/')}} style={{ width:'100%', background:'none', border:`0.5px solid ${t.border}`, color:t.textSecondary, borderRadius:50, padding:'8px', fontSize:12, cursor:'pointer', fontFamily:font }}>Salir</button>
      </div>
    </>
  );

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:t.bg, fontFamily:font, color:t.text }}>
      {/* Desktop sidebar */}
      {!hideSidebar && (
        <aside style={{ width:240, flexShrink:0, background:'#0a0a0a', borderRight:`0.5px solid ${t.border}`, display:'flex', flexDirection:'column', position:'sticky', top:0, height:'100vh', overflowY:'auto' }}>
          {/* Logo */}
          <div style={{ padding:'16px', display:'flex', alignItems:'center', gap:10, borderBottom:`0.5px solid ${t.border}` }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:C.logoGrad, display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:10, fontWeight:600 }}>T&D</span></div>
            <span style={{ fontSize:14, fontWeight:500 }}>{title}</span>
          </div>
          {sidebarContent}
        </aside>
      )}

      {/* Mobile header + overlay menu */}
      {hideSidebar && (
        <>
          <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:100, background:t.navBg, backdropFilter:'blur(12px)', borderBottom:`0.5px solid ${t.border}`, height:56, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px' }}>
            <button onClick={()=>setMenuOpen(true)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:t.text, padding:0 }}>☰</button>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background:C.logoGrad, display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:8, fontWeight:600 }}>T&D</span></div>
              <span style={{ fontSize:13, fontWeight:500 }}>{title}</span>
            </div>
            <UserAvatar url={profile?.avatar_url} name={nombre} size={28}/>
          </div>
          {/* Overlay */}
          {menuOpen && (
            <>
              <div onClick={()=>setMenuOpen(false)} style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', zIndex:200 }}/>
              <div style={{ position:'fixed', top:0, left:0, bottom:0, width:280, background:'#0a0a0a', zIndex:201, display:'flex', flexDirection:'column', animation:'slideIn 0.25s ease' }}>
                <div style={{ padding:'16px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:`0.5px solid ${t.border}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:C.logoGrad, display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:10, fontWeight:600 }}>T&D</span></div>
                    <span style={{ fontSize:14, fontWeight:500 }}>{title}</span>
                  </div>
                  <button onClick={()=>setMenuOpen(false)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:t.textSecondary }}>×</button>
                </div>
                {sidebarContent}
              </div>
            </>
          )}
          <style>{`@keyframes slideIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}`}</style>
        </>
      )}

      {/* Main content */}
      <main style={{ flex:1, minWidth:0, padding:hideSidebar?'72px 1rem 2rem':'2rem 2rem', maxWidth:900 }}>
        {children}
      </main>
    </div>
  );
}
